import * as React from "react";
import { Flex, FlexProps } from "../flex";
import {
  AriaTagGroupProps,
  AriaTagProps,
  useFocusRing,
  useTag,
  useTagGroup,
} from "react-aria";
import { Item, ListState, useListState } from "react-stately";
import { InputLabel } from "../input-label";
import { cx } from "../../utils/dom";
import { Text } from "../text";
import { InputError } from "../input-error";
import { Color, ColorVariant } from "../../utils/theme";
import { useTheme } from "../theme";
import { getRandomColor } from "../../utils/helpers";

interface TagProps<T> extends AriaTagProps<T>, TagVariantProps {
  state: ListState<T>;
}

function Tag<T>(props: TagProps<T>) {
  const theme = useTheme();
  const {
    activeColor = theme?.color ?? "gray",
    activeVariant = "solid",
    idleColor = "gray",
    idleVariant = "outline",
    item,
    state,
    autoColor,
  } = props;
  const tagRef = React.useRef(null);
  const { focusProps, isFocusVisible } = useFocusRing({ within: true });
  const { rowProps, gridCellProps, isSelected } = useTag(props, state, tagRef);

  const colors = React.useMemo(() => {
    if (autoColor) {
      const color = getRandomColor(item.textValue);
      return { active: color, idle: color };
    }

    return { active: activeColor, idle: idleColor };
  }, [activeColor, idleColor, autoColor]);

  return (
    <div
      ref={tagRef}
      {...rowProps}
      {...focusProps}
      data-focus-visible={isFocusVisible}
      data-is-toggle={true}
      className={cx("p-button", "size-sm")}
      data-variant={isSelected ? activeVariant : idleVariant}
      data-radius="full"
      data-color={isSelected ? colors.active : colors.idle}
    >
      <div {...gridCellProps}>{item.rendered}</div>
    </div>
  );
}

interface TagVariantProps {
  activeColor?: Color;
  activeVariant?: ColorVariant;
  idleColor?: Color;
  idleVariant?: ColorVariant;
  autoColor?: boolean;
}

interface TagGroupProps<T>
  extends AriaTagGroupProps<T>,
    FlexProps,
    TagVariantProps {}

export function TagGroup<T extends object>({
  wrap = "wrap",
  gap,
  columnGap,
  rowGap,
  align,
  justify,
  activeColor,
  activeVariant,
  idleColor,
  idleVariant,
  autoColor,
  ...props
}: TagGroupProps<T>) {
  const { label, description, errorMessage } = props;
  const tagGroupRef = React.useRef(null);
  const state = useListState(props);
  const { gridProps, labelProps, descriptionProps, errorMessageProps } =
    useTagGroup(props, state, tagGroupRef);

  return (
    <Flex stack className="tag-group">
      {label && <InputLabel {...labelProps}>{label}</InputLabel>}
      <Flex
        wrap={wrap}
        gap={gap}
        columnGap={columnGap}
        rowGap={rowGap}
        align={align}
        justify={justify}
        ref={tagGroupRef}
        {...gridProps}
      >
        {[...state.collection].map((item) => {
          return (
            <Tag
              autoColor={autoColor}
              activeColor={activeColor}
              activeVariant={activeVariant}
              idleColor={idleColor}
              idleVariant={idleVariant}
              key={item.key}
              state={state}
              item={item}
            ></Tag>
          );
        })}
      </Flex>
      {errorMessage && (
        <InputError {...errorMessageProps}>{errorMessage}</InputError>
      )}
      {description && (
        <Text as="div" {...descriptionProps}>
          {description}
        </Text>
      )}
    </Flex>
  );
}

export const TagGroupItem = Item;
